/**
 * TutorialOverlay - Main overlay component that handles spotlight and element targeting.
 * Uses portal rendering for proper z-index management.
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTutorial } from '../../contexts/TutorialContext';
import TutorialTooltip from './TutorialTooltip';
import './Tutorial.css';

function TutorialOverlay() {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    setTargetElement,
    setIsWaitingForElement,
    nextStep,
    prevStep,
    skipTutorial
  } = useTutorial();

  const [targetRect, setTargetRect] = useState(null);
  const [currentTargetEl, setCurrentTargetEl] = useState(null);
  const currentTargetElRef = useRef(null);
  const observerRef = useRef(null);
  const observerRafRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const pollStartTimeRef = useRef(null);
  // Prevents double-advancing on requiresClick steps
  const advancedStepRef = useRef(false);

  // Update target rectangle for spotlight
  // Uses functional update to skip re-render when rect values are unchanged
  const updateTargetRect = useCallback((element) => {
    if (!element) {
      setTargetRect(null);
      return;
    }

    const rect = element.getBoundingClientRect();
    const padding = currentStep?.highlightPadding ?? 8;

    const top = rect.top - padding;
    const left = rect.left - padding;
    const width = rect.width + (padding * 2);
    const height = rect.height + (padding * 2);

    setTargetRect(prev => {
      if (prev &&
          prev.top === top &&
          prev.left === left &&
          prev.width === width &&
          prev.height === height) {
        return prev; // Same rect, skip re-render
      }
      return { top, left, width, height, originalRect: rect };
    });
  }, [currentStep]);

  // Find target element by selector
  const findTargetElement = useCallback(() => {
    if (!currentStep) {
      setTargetElement(null);
      setTargetRect(null);
      return null;
    }

    // No selector means centered step (like completion)
    if (!currentStep.selector) {
      setTargetElement(null);
      setTargetRect(null);
      setIsWaitingForElement(false);
      currentTargetElRef.current = null;
      return null;
    }

    // Try primary selector
    let elements = document.querySelectorAll(currentStep.selector);

    // Filter by text content if specified
    if (currentStep.filterByText && elements.length > 0) {
      elements = Array.from(elements).filter(el =>
        el.textContent.toLowerCase().includes(currentStep.filterByText.toLowerCase())
      );
    }

    let element = elements[0];

    // Try fallback selectors if primary not found
    if (!element && currentStep.fallbackSelectors) {
      for (const fallback of currentStep.fallbackSelectors) {
        element = document.querySelector(fallback);
        if (element) break;
      }
    }

    if (element) {
      const isNewElement = element !== currentTargetElRef.current;
      // Distinguish first find from DOM node recreation (inline components)
      const wasAlreadyTracking = currentTargetElRef.current !== null;

      if (isNewElement) {
        currentTargetElRef.current = element;
        setCurrentTargetEl(element);
        // Only update context on first find for this step, not on DOM node
        // recreation, to prevent App re-render → PlaybackSection re-render →
        // button recreation → MutationObserver → findTargetElement loop
        if (!wasAlreadyTracking) {
          setTargetElement(element);
          setIsWaitingForElement(false);
        }
      }

      // Always scroll element into view so the spotlight and tooltip are visible
      const elRect = element.getBoundingClientRect();
      const isFullyVisible = elRect.top >= 0 && elRect.bottom <= window.innerHeight;
      if (!isFullyVisible) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Wait for scroll to complete before updating rect
        setTimeout(() => updateTargetRect(element), 400);
      } else {
        updateTargetRect(element);
      }
      return element;
    }

    if (currentTargetElRef.current !== null) {
      setCurrentTargetEl(null);
      currentTargetElRef.current = null;
    }
    return null;
  }, [currentStep, setTargetElement, setIsWaitingForElement, updateTargetRect]);

  // Start polling for element
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;

    const maxWait = currentStep?.maxWaitTime || 5000;
    pollStartTimeRef.current = Date.now();
    setIsWaitingForElement(true);

    pollIntervalRef.current = setInterval(() => {
      const element = findTargetElement();

      if (element) {
        // Found the element, stop polling
        stopPolling();
        return;
      }

      // Check timeout
      if (Date.now() - pollStartTimeRef.current > maxWait) {
        stopPolling();

        // Skip optional steps if element never appears
        if (currentStep?.optional) {
          nextStep();
        }
      }
    }, 200);
  }, [currentStep, findTargetElement, setIsWaitingForElement, nextStep]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollStartTimeRef.current = null;
  }, []);

  // Find element when step changes
  useEffect(() => {
    if (!isActive || !currentStep) {
      stopPolling();
      return;
    }

    // Reset element tracking for new step so context updates happen on first find
    currentTargetElRef.current = null;
    advancedStepRef.current = false;

    // First try to find the element immediately
    const element = findTargetElement();

    // If not found and waitForElement is enabled, start polling
    if (!element && currentStep.waitForElement) {
      startPolling();
    } else if (!element && currentStep.optional) {
      // Skip optional steps if element not found immediately and no waiting
      nextStep();
    }

    return () => stopPolling();
  }, [isActive, currentStep, currentStepIndex, findTargetElement, startPolling, stopPolling, nextStep]);

  // Update rect on scroll/resize
  useEffect(() => {
    if (!isActive) return;

    const handleUpdate = () => {
      const currentEl = currentTargetElRef.current;
      if (currentEl && currentEl.isConnected) {
        updateTargetRect(currentEl);
      } else if (currentEl) {
        // Element was disconnected, re-find it
        findTargetElement();
      }
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    // Observe DOM changes that might move elements
    // Debounced with requestAnimationFrame to avoid infinite loops:
    // MutationObserver → state update → re-render → DOM change → MutationObserver
    observerRef.current = new MutationObserver(() => {
      if (observerRafRef.current) return; // Already scheduled
      observerRafRef.current = requestAnimationFrame(() => {
        observerRafRef.current = null;
        const currentEl = currentTargetElRef.current;
        if (currentEl && currentEl.isConnected) {
          // Element still in DOM, just update its position
          updateTargetRect(currentEl);
        } else if (currentEl) {
          // Element was removed (e.g. by React re-render), re-find it
          findTargetElement();
        }
      });
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      observerRef.current?.disconnect();
      if (observerRafRef.current) {
        cancelAnimationFrame(observerRafRef.current);
        observerRafRef.current = null;
      }
    };
  }, [isActive, findTargetElement, updateTargetRect]);

  // Track target element size changes (e.g., video loading, recording area expanding)
  // ResizeObserver catches changes that MutationObserver misses (intrinsic size changes)
  useEffect(() => {
    if (!isActive || !currentTargetEl || typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(() => {
      if (currentTargetEl.isConnected) {
        updateTargetRect(currentTargetEl);
      }
    });

    resizeObserver.observe(currentTargetEl);

    return () => resizeObserver.disconnect();
  }, [isActive, currentTargetEl, updateTargetRect]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        skipTutorial();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        nextStep();
      } else if (e.key === 'ArrowLeft') {
        prevStep();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, skipTutorial, nextStep, prevStep]);

  // Block clicks outside the target element and tooltip, and handle requiresClick
  // Uses CSS selector matching to identify the target element, which is resilient
  // to DOM node recreation (e.g. inline function components that remount on render)
  useEffect(() => {
    if (!isActive) return;

    const handleClickBlock = (e) => {
      // Allow clicks on the tooltip
      const tooltip = document.querySelector('.tutorial-tooltip');
      if (tooltip && tooltip.contains(e.target)) {
        return; // Allow the click
      }

      // Check if click is on the target element
      // Use selector matching as primary check (resilient to DOM node recreation)
      // Fall back to ref for edge cases
      const targetEl = currentTargetElRef.current;
      const selector = currentStep?.selector;
      const isOnTarget =
        (selector && e.target.closest(selector)) ||
        (targetEl && targetEl.isConnected && targetEl.contains(e.target));

      if (isOnTarget) {
        // Block clicks on target if disableTargetClick is set
        if (currentStep?.disableTargetClick) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        // Block clicks on specific child elements
        if (currentStep?.blockChildSelectors) {
          for (const sel of currentStep.blockChildSelectors) {
            if (e.target.closest(sel)) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }
        }
        // Advance tutorial for requiresClick steps
        if (currentStep?.requiresClick && !advancedStepRef.current) {
          advancedStepRef.current = true;
          setTimeout(() => nextStep(), 100);
        }
        return; // Allow the click
      }

      // Block all other clicks
      e.preventDefault();
      e.stopPropagation();
    };

    // Use capture phase to intercept clicks before they reach elements
    document.addEventListener('click', handleClickBlock, true);
    return () => document.removeEventListener('click', handleClickBlock, true);
  }, [isActive, currentStep, nextStep]);

  // Handle overlay click (close on background click)
  const handleOverlayClick = useCallback((e) => {
    // Only close if clicking on the overlay itself, not the tooltip
    if (e.target.classList.contains('tutorial-spotlight')) {
      // Allow the click to pass through to the target element
      // Don't close the tutorial on spotlight click
    }
  }, []);

  if (!isActive) return null;

  // Render using portal for z-index management
  return ReactDOM.createPortal(
    <div className="tutorial-overlay">
      {/* Spotlight overlay with cutout */}
      <div className="tutorial-spotlight" onClick={handleOverlayClick}>
        {targetRect && (
          <div
            className="tutorial-spotlight-hole"
            style={{
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height
            }}
          />
        )}
      </div>

      {/* Tooltip */}
      <TutorialTooltip
        step={currentStep}
        targetRect={targetRect}
        stepIndex={currentStepIndex}
        totalSteps={totalSteps}
        onNext={nextStep}
        onPrev={prevStep}
        onSkip={skipTutorial}
      />
    </div>,
    document.body
  );
}

export default TutorialOverlay;
